import {useLazyQuery, useQuery} from '@apollo/client'
import {useEffect, useRef, useState, useCallback} from 'react'
import {DateInput} from 'app/tfi/components/DateInput'
import {GET_ALL_POLICIES_TYPES, GET_ALL_POLICIES_STATUSES} from '../../gql/queries/policiesQueries'
import {GET_ALL_PIPELINES} from '../../gql/queries/pipelines'
import {IResPoliciesType} from '../../types/policies'
import {IResPipelines} from '../../types/pipelines'
import {GET_ALL_INSURANCE_OPERATORS_LIST} from '../../gql/queries/carriersQuery'
import {GET_STATES_EU} from '../../gql/queries/statesEUQuery'
import {GET_ALL_USERS_LIST} from '../../gql/queries/usersQuery'
import {IResCarriers} from '../../types/carriers'
import {IResStates} from '../../types/statesEU'
import {Col, Form, Row, Spinner, Card} from 'react-bootstrap'
import {toast} from 'react-toastify'
import Select from 'react-select'
import {MultiSelectWithActions} from '../../components/MultiSelectWithActions'
import {GET_POLICY_DASHBOARD_SUMMARY} from '../../gql/queries/reportsQuery'
import {PolicyDashboardChart} from './components/PolicyDashboardChart'
import {AgentsLeaderboard} from './components/AgentsLeaderboard'
import {PolicyTypeTrendsChart} from './components/PolicyTypeTrendsChart'
import {PolicyTypeDistributionChart} from './components/PolicyTypeDistributionChart'
import {CarrierPoliciesTable} from './components/CarrierPoliciesTable'
import {CarrierApplicantsTable} from './components/CarrierApplicantsTable'
import {StatePoliciesMap} from './components/StatePoliciesMap'
import {StatusPoliciesChart} from './components/StatusPoliciesChart'
import {AdvisorsCompletedPoliciesTable} from './components/AdvisorsCompletedPoliciesTable'
import {AgentsRenewalRateTable} from './components/AgentsRenewalRateTable'
import {KTIcon} from '../../../../_metronic/helpers'
import {parseId, isAdministrator, hasPermission} from '../../helpers'
import {useGetUser} from '../../store/userStore'
import {ALL_PERMISSIONS} from '../../helpers/consts'
import {LeadTotalsKpi} from '../homeReports/components/LeadTotalsKpi'

interface IPeriodData {
  period: string
  periodLabel: string
  policiesCount: number
  applicantsCount: number
}

interface IPolicyDashboardSummary {
  totalPolicies: number
  totalApplicants: number
  totalPoliciesLastPeriod: number
  totalApplicantsLastPeriod: number
  growthPercentage: number
  periodData: IPeriodData[]
}

interface IResDashboard {
  policyDashboardSummary: IPolicyDashboardSummary
}

export const NewDashboardPage = () => {
  const defaultLeadPipelineId = 'cGlwZWxpbmVfbm9kZToxNg'
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const permissions = useGetUser((user) => user?.user?.permissions)
  const isAgente = userGroups.includes('Agente')
  const isSupervisor = userGroups.includes('Supervisor')
  const isAdmin = permissions ? isAdministrator(permissions) : false

  // Solo mostrar el leaderboard si NO es Asesor
  const canSeeLeaderboard = !isAgente

  // Mostrar completed policies si es supervisor o admin (incluye agentes con delegated_users via backend)
  const canSeeCompletedPolicies = isSupervisor || isAdmin

  // Mostrar renewal rate solo si es supervisor o admin (NO para agentes)
  const canSeeRenewalRate = isSupervisor || isAdmin

  const {
    data: dataPolicyTypes,
    loading: loadingPolicyTypes,
    error: errorPolicyTypes,
  } = useQuery<IResPoliciesType>(GET_ALL_POLICIES_TYPES, {fetchPolicy: 'no-cache'})
  const {
    data: dataPipelines,
    loading: loadingPipelines,
    error: errorPipelines,
  } = useQuery<IResPipelines>(GET_ALL_PIPELINES, {fetchPolicy: 'no-cache'})
  const {
    data: dataCarriers,
    loading: loadingCarriers,
    error: errorCarriers,
  } = useQuery<IResCarriers>(GET_ALL_INSURANCE_OPERATORS_LIST, {fetchPolicy: 'no-cache'})
  const {
    data: dataStates,
    loading: loadingStates,
    error: errorStates,
  } = useQuery<IResStates>(GET_STATES_EU, {fetchPolicy: 'no-cache'})

  const {
    data: dataPolicyStatuses,
    loading: loadingPolicyStatuses,
    error: errorPolicyStatuses,
  } = useQuery(GET_ALL_POLICIES_STATUSES, {fetchPolicy: 'no-cache'})

  const {
    data: dataUsers,
    loading: loadingUsers,
    error: errorUsers,
  } = useQuery(GET_ALL_USERS_LIST, {fetchPolicy: 'no-cache'})

  // Rango de fechas por defecto: del 1 de enero del año en curso hasta hoy
  const [dates, setDates] = useState(() => {
    const today = new Date()
    const startOfYear = new Date(today.getFullYear(), 0, 1, 0, 0, 0, 0)
    const endOfToday = new Date(today.setHours(23, 59, 59, 999))
    return [startOfYear, endOfToday]
  })

  const [dateCriteria, setDateCriteria] = useState<'submitted' | 'effective'>('effective')
  const [policyTypeSelect, setPolicyTypeSelect] = useState<null | {value: string; label: string}>(
    null
  )
  const [carrierSelect, setCarrierSelect] = useState<null | {value: string; label: string}>(null)
  const [stateSelect, setStateSelect] = useState<null | {value: string; label: string}>(null)
  const [statusSelect, setStatusSelect] = useState<Array<{value: string; label: string}>>([])
  const [agentSelect, setAgentSelect] = useState<Array<{value: string; label: string}>>([])
  const [leadPipelineId, setLeadPipelineId] = useState(defaultLeadPipelineId)
  const leadPipelineInitialized = useRef(false)

  const [getDashboardData, {data, loading, error}] = useLazyQuery<IResDashboard>(
    GET_POLICY_DASHBOARD_SUMMARY,
    {
      fetchPolicy: 'no-cache',
    }
  )

  useEffect(() => {
    if (errorCarriers) toast.error(`Error: ${errorCarriers.message}`)
  }, [errorCarriers])

  useEffect(() => {
    if (errorPolicyTypes) toast.error(`Error: ${errorPolicyTypes.message}`)
  }, [errorPolicyTypes])

  useEffect(() => {
    if (errorStates) toast.error(`Error: ${errorStates.message}`)
  }, [errorStates])

  useEffect(() => {
    if (errorPolicyStatuses) toast.error(`Error: ${errorPolicyStatuses.message}`)
  }, [errorPolicyStatuses])

  useEffect(() => {
    if (errorUsers) toast.error(`Error: ${errorUsers.message}`)
  }, [errorUsers])
  useEffect(() => {
    if (errorPipelines) toast.error(`Error: ${errorPipelines.message}`)
  }, [errorPipelines])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  useEffect(() => {
    if (!dataPipelines) return
    if (leadPipelineInitialized.current) return
    const pipelines = dataPipelines.allPipelines.edges.map(({node}) => node)
    if (pipelines.length === 0) return

    leadPipelineInitialized.current = true

    const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '')

    const exactSeguroDeSalud = pipelines.find((pipeline) => {
      const normalizedName = normalize(pipeline.name || '')
      return normalizedName === 'segurodesalud'
    })

    const hasDefault = pipelines.some((pipeline) => pipeline.id === defaultLeadPipelineId)
    const preferredId = exactSeguroDeSalud?.id || (hasDefault ? defaultLeadPipelineId : undefined)

    setLeadPipelineId(preferredId || (pipelines[0].id as string))
  }, [dataPipelines])

  const formatDate = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const startDateValue = dates?.[0] ? formatDate(dates[0]) : ''
  const endDateValue = dates?.[1] ? formatDate(dates[1]) : ''

  const fetchData = useCallback(() => {
    const [dateFrom, dateTo] = dates

    getDashboardData({
      variables: {
        dateFrom: formatDate(dateFrom),
        dateTo: formatDate(dateTo),
        dateCriteria,
        carrier: carrierSelect?.value ? [parseId(carrierSelect.value).toString()] : undefined,
        stateEu: stateSelect?.value ? [stateSelect.value] : undefined,
        policyType: policyTypeSelect?.value ? [parseId(policyTypeSelect.value).toString()] : undefined,
        status: statusSelect.length > 0 ? statusSelect.map(s => s.value) : undefined,
        agent: agentSelect.length > 0 ? agentSelect.map(a => parseId(a.value).toString()) : undefined,
      },
    })
  }, [dates, dateCriteria, policyTypeSelect, carrierSelect, stateSelect, statusSelect, agentSelect, getDashboardData])

  useEffect(() => {
    if (dates.length === 2) {
      fetchData()
    }
  }, [dates, dateCriteria, policyTypeSelect, carrierSelect, stateSelect, statusSelect, agentSelect, fetchData])

  const summary = data?.policyDashboardSummary

  // Prepare filter values for passing to components
  const statusFilter = statusSelect.length > 0 ? statusSelect.map(s => s.value) : undefined
  const agentFilter = agentSelect.length > 0 ? agentSelect.map(a => parseId(a.value).toString()) : undefined

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num)
  }

  const formatPercentage = (num: number) => {
    return num >= 0 ? `+${num.toFixed(2)}%` : `${num.toFixed(2)}%`
  }

  return (
    <div>
      {/* <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between'>
        <h1 className='d-flex align-items-center'>
          POLICIES DASHBOARD
          {(loadingCarriers || loadingPolicyTypes || loadingStates || loading) && (
            <Spinner animation='border' size='sm' className='ms-1' />
          )}
        </h1>
      </div> */}

      {/* Filters Section */}
      <Row className='mt-3 g-3'>
        <Col sm={12} md={3}>
          <small>Date Range</small>
          <Row className='g-2'>
            <Col xs={12} md={6}>
              <DateInput
                id='dateFrom'
                name='dateFrom'
                placeholder='Date From'
                value={startDateValue}
                onChange={(_, value) => {
                  if (!value) return
                  const date = new Date(`${value}T00:00:00`)
                  date.setHours(0, 0, 0, 0)
                  let currentEnd = dates?.[1] ? new Date(dates[1]) : new Date(date)
                  currentEnd.setHours(23, 59, 59, 999)
                  if (currentEnd < date) {
                    currentEnd = new Date(date)
                    currentEnd.setHours(23, 59, 59, 999)
                  }
                  setDates([date, currentEnd])
                }}
              />
            </Col>
            <Col xs={12} md={6}>
              <DateInput
                id='dateTo'
                name='dateTo'
                placeholder='Date To'
                value={endDateValue}
                onChange={(_, value) => {
                  if (!value) return
                  const date = new Date(`${value}T00:00:00`)
                  date.setHours(23, 59, 59, 999)
                  let currentStart = dates?.[0] ? new Date(dates[0]) : new Date(date)
                  currentStart.setHours(0, 0, 0, 0)
                  if (date < currentStart) {
                    currentStart = new Date(date)
                    currentStart.setHours(0, 0, 0, 0)
                  }
                  setDates([currentStart, date])
                }}
              />
            </Col>
          </Row>
        </Col>
        <Col sm={12} md={3}>
          <small>Date Criteria</small>
          <Select
            options={[
              {value: 'effective', label: 'Policies Effective'},
              {value: 'submitted', label: 'Policies Submitted'},
            ]}
            value={
              dateCriteria === 'effective'
                ? {value: 'effective', label: 'Policies Effective'}
                : {value: 'submitted', label: 'Policies Submitted'}
            }
            onChange={(selected) =>
              setDateCriteria((selected?.value || 'effective') as 'submitted' | 'effective')
            }
          />
        </Col>
        <Col sm={12} md={2}>
          <small>Policy Type</small>
          <Select
            options={
              dataPolicyTypes?.allPoliciesType.edges.map(({node}) => ({
                label: node.typePolicy,
                value: node.id,
              })) || []
            }
            placeholder='All'
            noOptionsMessage={() => 'Not found'}
            value={policyTypeSelect}
            onChange={setPolicyTypeSelect}
            isClearable
          />
        </Col>
        <Col sm={12} md={2}>
          <small>Carriers</small>
          <Select
            options={
              dataCarriers?.allInsuranceOperators.edges.map(({node}) => ({
                label: node.name,
                value: node.id,
              })) || []
            }
            placeholder='All'
            noOptionsMessage={() => 'Not found'}
            value={carrierSelect}
            onChange={setCarrierSelect}
            isClearable
          />
        </Col>
        <Col sm={12} md={2}>
          <small>States</small>
          <Select
            options={
              dataStates?.allStates.edges.map(({node}) => ({
                label: node.name,
                value: node.code,
              })) || []
            }
            placeholder='All'
            noOptionsMessage={() => 'Not found'}
            value={stateSelect}
            onChange={setStateSelect}
            isClearable
          />
        </Col>
      </Row>

      {/* Additional Filters Row */}
      <Row className='mt-3 g-3'>
        <Col sm={12} md={4}>
          <small>Policy Status</small>
          <MultiSelectWithActions
            value={statusSelect}
            options={
              (() => {
                const statuses = dataPolicyStatuses?.allPolicyStatuses?.edges || []
                const groupedByCategory: {[key: string]: any[]} = {}

                // Group statuses by category
                statuses.forEach(({node}: any) => {
                  const category = node.category || 'OTHER'
                  if (!groupedByCategory[category]) {
                    groupedByCategory[category] = []
                  }
                  groupedByCategory[category].push({
                    value: node.code,
                    label: node.title,
                  })
                })

                // Convert to react-select group format
                return Object.keys(groupedByCategory).map(category => ({
                  label: category.replace(/_/g, ' '),
                  options: groupedByCategory[category]
                }))
              })()
            }
            onChange={setStatusSelect}
            placeholder='All statuses'
          />
        </Col>
        {!isAgente && (
          <Col sm={12} md={4}>
            <small>Agent</small>
            <MultiSelectWithActions
              value={agentSelect}
              options={
                dataUsers?.allUsersList?.map((user: any) => ({
                  value: user.id,
                  label: `${user.firstName} ${user.lastName} - ${user.email}`,
                })) || []
              }
              onChange={setAgentSelect}
              placeholder='All agents'
            />
          </Col>
        )}
        {(isSupervisor || isAdmin) && (
          <>
            <Col sm={12} md={2}>
              <small>Lead Pipeline</small>
              <Select
                options={
                  dataPipelines?.allPipelines.edges.map(({node}) => ({
                    label: node.name,
                    value: node.id,
                  })) || []
                }
                placeholder='All Pipelines'
                noOptionsMessage={() => 'Not found'}
                value={
                  dataPipelines?.allPipelines.edges
                    .map(({node}) => ({label: node.name, value: node.id}))
                    .find((opt) => opt.value === leadPipelineId) || null
                }
                onChange={(opt) => setLeadPipelineId(opt?.value || '')}
                isClearable
                isLoading={loadingPipelines}
              />
            </Col>
            <Col sm={12} md={2} className='d-flex flex-column'>
              <small className='opacity-0'>Refresh</small>
              <button
                type='button'
                className='btn btn-primary w-100'
                onClick={fetchData}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-2' />
                    Refreshing
                  </>
                ) : (
                  <>
                    <KTIcon iconName='arrows-loop' className='fs-4 me-2' />
                    Refresh
                  </>
                )}
              </button>
            </Col>
          </>
        )}
      </Row>

      {/* Summary Cards */}
      {summary && (
        <>
          <Row className='mt-4 g-3'>
            <Col sm={12} md={6} lg={2}>
              <Card className='card-flush h-100'>
                <Card.Body className='d-flex flex-column'>
                  <div className='d-flex align-items-center mb-2'>
                    <KTIcon iconName='chart-line-up' className='fs-2x text-primary me-2' />
                    <span className='text-gray-700 fw-bold fs-6'>Policies</span>
                  </div>
                  <div className='d-flex align-items-baseline'>
                    <span className='fs-2hx fw-bold text-dark me-2'>
                      {formatNumber(summary.totalPolicies)}
                    </span>
                    <span className='text-muted fs-7'>This Period</span>
                  </div>
                  <div className='mt-2'>
                    <span className='text-muted fs-8'>
                      {formatNumber(summary.totalPoliciesLastPeriod)} last period
                    </span>
                  </div>
                  <div className='mt-1'>
                    <span
                      className={`badge ${summary.growthPercentage >= 0 ? 'badge-success' : 'badge-danger'}`}
                    >
                      {formatPercentage(summary.growthPercentage)}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={12} md={6} lg={2}>
              <Card className='card-flush h-100'>
                <Card.Body className='d-flex flex-column'>
                  <div className='d-flex align-items-center mb-2'>
                    <KTIcon iconName='people' className='fs-2x text-success me-2' />
                    <span className='text-gray-700 fw-bold fs-6'>Applicants</span>
                  </div>
                  <div className='d-flex align-items-baseline'>
                    <span className='fs-2hx fw-bold text-dark me-2'>
                      {formatNumber(summary.totalApplicants)}
                    </span>
                    <span className='text-muted fs-7'>This Period</span>
                  </div>
                  <div className='mt-2'>
                    <span className='text-muted fs-8'>
                      {formatNumber(summary.totalApplicantsLastPeriod)} last period
                    </span>
                  </div>
                  <div className='mt-1'>
                    <span
                      className={`badge ${summary.growthPercentage >= 0 ? 'badge-success' : 'badge-danger'}`}
                    >
                      {formatPercentage(summary.growthPercentage)}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={12} md={6} lg={2}>
              <Card className='card-flush h-100'>
                <Card.Body className='d-flex flex-column'>
                  <div className='d-flex align-items-center mb-2'>
                    <KTIcon iconName='timer' className='fs-2x text-info me-2' />
                    <span className='text-gray-700 fw-bold fs-6'>Period</span>
                  </div>
                  <div className='d-flex align-items-baseline'>
                    <span className='fs-4 fw-bold text-dark'>
                      {dates[0].toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}{' '}
                      -{' '}
                      {dates[1].toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                  <div className='mt-2'>
                    <span className='text-muted fs-8'>
                      {dateCriteria === 'submitted' ? 'Submitted Date' : 'Effective Date'}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={12} md={6} lg={2}>
              <Card className='card-flush h-100'>
                <Card.Body className='d-flex flex-column'>
                  <div className='d-flex align-items-center mb-2'>
                    <KTIcon iconName='chart-pie-simple' className='fs-2x text-warning me-2' />
                    <span className='text-gray-700 fw-bold fs-6'>Avg Applicants</span>
                  </div>
                  <div className='d-flex align-items-baseline'>
                    <span className='fs-2hx fw-bold text-dark me-2'>
                      {summary.totalPolicies > 0
                        ? (summary.totalApplicants / summary.totalPolicies).toFixed(2)
                        : '0.00'}
                    </span>
                    <span className='text-muted fs-7'>per policy</span>
                  </div>
                  <div className='mt-2'>
                    <span className='text-muted fs-8'>
                      Average ratio
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            {(isSupervisor || isAdmin) && (
              <LeadTotalsKpi pipelineId={leadPipelineId} dates={dates} />
            )}
          </Row>

          {/* Charts Section */}
          <Row className='mt-4 g-3'>
            <Col sm={12} lg={canSeeLeaderboard ? 6 : 12}>
              <PolicyDashboardChart
                periodData={summary.periodData}
                dateCriteria={dateCriteria}
                loading={loading}
              />
            </Col>
            {canSeeLeaderboard && (
              <Col sm={12} lg={6}>
                <AgentsLeaderboard
                  dateFrom={formatDate(dates[0])}
                  dateTo={formatDate(dates[1])}
                  dateCriteria={dateCriteria}
                  carrier={carrierSelect?.value ? parseId(carrierSelect.value).toString() : undefined}
                  stateEu={stateSelect?.value}
                  policyType={policyTypeSelect?.value ? parseId(policyTypeSelect.value).toString() : undefined}
                  status={statusFilter}
                  agent={agentFilter}
                  loading={loading}
                />
              </Col>
            )}
          </Row>

          {/* Policy Type Analytics Section */}
          <Row className='mt-4 g-3'>
            <Col sm={12} lg={6}>
              <PolicyTypeTrendsChart
                dateFrom={formatDate(dates[0])}
                dateTo={formatDate(dates[1])}
                dateCriteria={dateCriteria}
                carrier={carrierSelect?.value ? parseId(carrierSelect.value).toString() : undefined}
                stateEu={stateSelect?.value}
                policyType={policyTypeSelect?.value ? parseId(policyTypeSelect.value).toString() : undefined}
                status={statusFilter}
                agent={agentFilter}
                loading={loading}
              />
            </Col>
            <Col sm={12} lg={6}>
              <PolicyTypeDistributionChart
                dateFrom={formatDate(dates[0])}
                dateTo={formatDate(dates[1])}
                dateCriteria={dateCriteria}
                carrier={carrierSelect?.value ? parseId(carrierSelect.value).toString() : undefined}
                stateEu={stateSelect?.value}
                policyType={policyTypeSelect?.value ? parseId(policyTypeSelect.value).toString() : undefined}
                status={statusFilter}
                agent={agentFilter}
                loading={loading}
              />
            </Col>
          </Row>

          {/* Carrier Distribution Tables */}
          <Row className='mt-4 g-3'>
            <Col sm={12} lg={6}>
              <CarrierPoliciesTable
                dateFrom={formatDate(dates[0])}
                dateTo={formatDate(dates[1])}
                dateCriteria={dateCriteria}
                carrier={carrierSelect?.value ? parseId(carrierSelect.value).toString() : undefined}
                stateEu={stateSelect?.value}
                policyType={policyTypeSelect?.value ? parseId(policyTypeSelect.value).toString() : undefined}
                status={statusFilter}
                agent={agentFilter}
                loading={loading}
              />
            </Col>
            <Col sm={12} lg={6}>
              <CarrierApplicantsTable
                dateFrom={formatDate(dates[0])}
                dateTo={formatDate(dates[1])}
                dateCriteria={dateCriteria}
                carrier={carrierSelect?.value ? parseId(carrierSelect.value).toString() : undefined}
                stateEu={stateSelect?.value}
                policyType={policyTypeSelect?.value ? parseId(policyTypeSelect.value).toString() : undefined}
                status={statusFilter}
                agent={agentFilter}
                loading={loading}
              />
            </Col>
          </Row>

          {/* State Policies Map and Status Distribution */}
          <Row className='mt-4 g-3'>
            <Col sm={12} lg={6}>
              <StatePoliciesMap
                dateFrom={formatDate(dates[0])}
                dateTo={formatDate(dates[1])}
                dateCriteria={dateCriteria}
                carrier={carrierSelect?.value ? parseId(carrierSelect.value).toString() : undefined}
                policyType={policyTypeSelect?.value ? parseId(policyTypeSelect.value).toString() : undefined}
                status={statusFilter}
                agent={agentFilter}
                loading={loading}
              />
            </Col>
            <Col sm={12} lg={6}>
              <StatusPoliciesChart
                dateFrom={formatDate(dates[0])}
                dateTo={formatDate(dates[1])}
                dateCriteria={dateCriteria}
                carrier={carrierSelect?.value ? parseId(carrierSelect.value).toString() : undefined}
                stateEu={stateSelect?.value}
                policyType={policyTypeSelect?.value ? parseId(policyTypeSelect.value).toString() : undefined}
                status={statusFilter}
                agent={agentFilter}
                loading={loading}
              />
            </Col>
          </Row>

          {/* Advisors Table - Full Width */}
          {canSeeCompletedPolicies && (
            <Row className='mt-4'>
              <Col sm={12}>
                <AdvisorsCompletedPoliciesTable
                  dateFrom={formatDate(dates[0])}
                  dateTo={formatDate(dates[1])}
                  dateCriteria={dateCriteria}
                  carrier={carrierSelect?.value ? parseId(carrierSelect.value).toString() : undefined}
                  stateEu={stateSelect?.value}
                  policyType={policyTypeSelect?.value ? parseId(policyTypeSelect.value).toString() : undefined}
                  agent={agentFilter}
                  loading={loading}
                />
              </Col>
            </Row>
          )}

          {/* Agents Performance Metrics Table - Full Width */}
          {canSeeRenewalRate && (
            <Row className='mt-4'>
              <Col sm={12}>
                <AgentsRenewalRateTable
                  dateFrom={formatDate(dates[0])}
                  dateTo={formatDate(dates[1])}
                  dateCriteria={dateCriteria}
                  carrier={carrierSelect?.value ? parseId(carrierSelect.value).toString() : undefined}
                  stateEu={stateSelect?.value}
                  policyType={policyTypeSelect?.value ? parseId(policyTypeSelect.value).toString() : undefined}
                  agent={agentFilter}
                  loading={loading}
                />
              </Col>
            </Row>
          )}
        </>
      )}

      {!summary && !loading && (
        <div className='mt-4 text-center text-muted'>
          <p>Select a date range to view dashboard data</p>
        </div>
      )}
    </div>
  )
}
